/* eslint-disable react/prop-types */
import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { useHistory } from 'react-router-dom'

import { ContainedButton, ThemeButton, CircularProgress, OutlinedButton } from '../Controls'
import { clearForm, buildPostObject } from './common'
import { setFormStatusError, setFormStatusSuccess, formStatusSelector, clearFormStatus, formStatusError } from '../../redux'
import { setBlocking, clearBlocking } from '../../containers/GlobalBusyCursor'
import { useTranslation } from 'react-i18next'

const defaultAutoHideDuration = 2000
const pageButtonWidth = '100px'

const ThemeButtonStyles = styled(ThemeButton)`
  min-width: ${pageButtonWidth};
`

const SaveButtonStyles = styled(ContainedButton)`
  min-width: ${pageButtonWidth};
`

const InsertRowButtonStyles = styled(OutlinedButton)`
  min-width: ${pageButtonWidth};
`

const SaveButtonContainer = styled.div`
  position: relative;
  display: inline-block;
`

const CircularProgressContainer = styled.div`
  position: absolute;
  z-index: 1;
  top: 50%;
  left: 50%;
  margin-top: -12px;
  margin-left: -12px;
  display: inline-block;
`

/**
 * CancelButton is a basic user interaction component that executes a function based on a user click.
 *
 * @param {object} props component props
 * @returns {CancelButton} CancelButton component
 */
export const CancelButton = props => {
  const { t } = useTranslation()
  const { arrRefs, formTemplate, label, ...finalProps } = props
  const onClickCancel = useCallback(() => {
    props.onClick && props.onClick()
  }, [])
  return (
    <ThemeButtonStyles variant={'outlined'} {...finalProps} onClick={onClickCancel} noTranslate={true}>{t('buttons.cancel')}</ThemeButtonStyles>
  )
}

CancelButton.propTypes = {
  /**
   id for the control.
   */
  id: PropTypes.string,
  /**
   Function that is executed when the button is clicked.
   */
  onClick: PropTypes.func,
  /**
   Makes the button full width within the parent container.
   */
  fullWidth: PropTypes.bool,
  /**
   Determines the button style.
   */
  variant: PropTypes.oneOf(['contained', 'outlined', 'text']),
  /**
   Determines the button size.
   */
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  /**
   Determines the button's color.
   */
  color: PropTypes.oneOf(['primary', 'secondary', 'success', 'error', 'info', 'warning', 'default']),
  /**
   Determines if the button is disabled.
   */
  disabled: PropTypes.bool,
  /**
  Icon element placed before the children.
   */
  startIcon: PropTypes.node,
  /**
  Icon element placed after the children.
   */
  endIcon: PropTypes.node,
  /**
  Override for the button's background color. If this is supplied then the color prop is ignored.
   */
  backgroundColor: PropTypes.string,
  /**
  Override for the button's background hover color. Only valid if backgroundColor is set.
   */
  hoverBackgroundColor: PropTypes.string,
  /**
  Override for the button's text color. Only valid if backgroundColor is set.
   */
  textColor: PropTypes.string,
  label: PropTypes.string,
}

CancelButton.defaultProps = {
  variant: 'outlined'
}

/**
 * ClearButton is a basic user interaction component that sets each form control to its default value based on a user click.
 *
 * @param {object} props component props
 * @returns {ClearButton} ClearButton component
 */
export const ClearButton = props => {
  const { t } = useTranslation()
  const { clearLabel, arrRefs, formTemplate, ...finalProps } = props
  const clear = useCallback(() => {
    clearForm(arrRefs)
    clearFormStatus()
    props.onClick && props.onClick()
  }, [])

  return (
    <ThemeButtonStyles variant={'outlined'} {...finalProps} onClick={clear} noTranslate={true}>{t(`${clearLabel}`)}</ThemeButtonStyles>
  )
}

ClearButton.propTypes = {
  /**
   id for the control.
   */
  id: PropTypes.string,
  /** 
   * arrRefs is returned by the hook useFormPageHandler.
   */
  arrRefs: PropTypes.array,
  /**
   Function that is executed when the button is clicked.
   */
  onClick: PropTypes.func,
  /**
   Makes the button full width within the parent container.
   */
  fullWidth: PropTypes.bool,
  /**
   Determines the button style.
   */
  variant: PropTypes.oneOf(['contained', 'outlined', 'text']),
  /**
   Determines the button size.
   */
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  /**
   Determines the button's color.
   */
  color: PropTypes.oneOf(['primary', 'secondary', 'success', 'error', 'info', 'warning', 'default']),
  /**
   Determines if the button is disabled.
   */
  disabled: PropTypes.bool,
  /**
  Icon element placed before the children.
   */
  startIcon: PropTypes.node,
  /**
  Icon element placed after the children.
   */
  endIcon: PropTypes.node,
  /**
  Override for the button's background color. If this is supplied then the color prop is ignored.
   */
  backgroundColor: PropTypes.string,
  /**
  Override for the button's background hover color. Only valid if backgroundColor is set.
   */
  hoverBackgroundColor: PropTypes.string,
  /**
  Override for the button's text color. Only valid if backgroundColor is set.
   */
  textColor: PropTypes.string,
}

CancelButton.defaultProps = {
  variant: 'outlined'
}

/**
 * SaveButton is a basic user interaction component when clicked, runs the validator for each form control and if successful buiuld the post object and then posts the object using the input post service.
 *
 * @param {object} props component props
 * @returns {SaveButton} SaveButton component
 */
export const SaveButton = props => {
  const { t } = useTranslation()
  const { arrRefs, postService, onPostComplete, saveLabel, onBeforePost, pathname, search, formTemplate, arrKeyInBackendObject, feedBack, history, ...finalProps } = props
  const internalHistory = useHistory()
  const historyToUse = history !== undefined ? history : internalHistory
  const [loading, setLoading] = useState(false)

  const setClearSuccess = () => {
    setTimeout(() => {
      if (formStatusSelector() !== formStatusError) {
        clearFormStatus()
      }
    }, defaultAutoHideDuration)
  }

  const save = useCallback(async () => {
    if (formStatusSelector() === formStatusError) {
      return false
    }
    let postObject = buildPostObject(arrRefs, formTemplate, arrKeyInBackendObject)
    if (postObject !== null) {
      if (typeof onBeforePost === 'function') {
        postObject = onBeforePost(postObject)
      }
      try {
        if (feedBack) {
          setBlocking()
          setLoading(true)
        }
        await postService(postObject)
        if (feedBack) {
          clearBlocking()
          setLoading(false)
        }
        if (feedBack && !pathname) {
          setFormStatusSuccess()
          setClearSuccess()
        }
        onPostComplete && onPostComplete()
        if (pathname) {
          historyToUse.push({
            pathname,
            search,
            state: { ...postObject }
          })
        }
      } catch {
        if (feedBack) {
          clearBlocking()
          setLoading(false)
        }
      }
    } else if (feedBack) {
      setFormStatusError()
    }
  }, [])

  return (
    <>
      <SaveButtonContainer>
        <SaveButtonStyles
          {...finalProps}
          disabled={loading || props.disabled} 
          // color={feedBack ? formStatus || props.color || 'primary' : props.color}
          variant={props.variant || 'contained'}
          onClick={save}
          noTranslate={true}
        >
          {saveLabel || t('buttons.save')}
        </SaveButtonStyles>
        {loading && <CircularProgressContainer><CircularProgress size={24} /></CircularProgressContainer>}
      </SaveButtonContainer>
    </>
  )
}

SaveButton.propTypes = {
  /**
   id for the control.
   */
  id: PropTypes.string,
  /**
   Determines the button style.
   */
  variant: PropTypes.oneOf(['contained', 'outlined', 'text']),
  /**
   Determines the button size.
   */
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  /**
   Determines the button's color.
   */
  color: PropTypes.oneOf(['primary', 'secondary', 'success', 'error', 'info', 'warning', 'default']),
  /** 
   * arrRefs is returned by the hook useFormPageHandler.
   */
  arrRefs: PropTypes.array,
  /** 
   * Promised based pas service to post the object constructed by the form library.
   */
  postService: PropTypes.func, 
  /** 
   * Function to call after the post is conplete and successful.
   */
  onPostComplete: PropTypes.func,
  /** 
   * Alternative label for the save button. This will be translated.
   */
  saveLabel: PropTypes.string,
  /** 
   * Called before the post with the post object as an argument. It must return the post object that will be posted to the backend.
   */
  onBeforePost: PropTypes.func,
  /** 
   * The pathname to navigate to after the post.
   */
  pathname: PropTypes.string,
  /** 
   * The search parameter for the pathname if required.
   */
  search: PropTypes.string, 
  /** 
   * The form template object for the control.
   */
  formTemplate: PropTypes.array,
  /** 
   * If set, the save button will be disabled during the post and then set to green if successful for 4 seconds. If the form does not validate then the button is set to the color red.
   * Also, the loading cursor will be displayed in the save button as opposed to displaying the global busy cursor.
   */
  feedBack: PropTypes.bool,
  /** 
   * history object
   */ 
  history: PropTypes.object,
  /** 
   * If a table is being used to input controls, this is the key in the post object for the array of objects with the values for the controls.
   */
  arrKeyInBackendObject: PropTypes.string,
  /**
   Determines if the button is disabled.
   */
  disabled: PropTypes.bool
}

SaveButton.defaultProps = {
  variant: 'contained',
  feedBack: true,
  disabled: false
}

/**
 * NextButton operates as the SaveButton except it defaults to 'Next' as the label. 
 * 
 * @param {object} props component props
 * @returns {NextButton} NextButton component
 */
export const NextButton = props =>
  <SaveButton {...props} saveLabel='buttons.next' />

NextButton.propTypes = {
  /**
   id for the control.
   */
  id: PropTypes.string,
  /** 
   * arrRefs is returned by the hook useFormPageHandler.
   */
  arrRefs: PropTypes.array,
  /** 
   * Promised based pas service to post the object constructed by the form library.
   */
  postService: PropTypes.func, 
  /** 
   * Function to call after the post is conplete and successful.
   */
  onPostComplete: PropTypes.func,
  /** 
   * Called before the post with the post object as an argument. It must return the post object that will be posted to the backend.
   */
  onBeforePost: PropTypes.func,
  /** 
   * The pathname to navigate to after the post.
   */
  pathname: PropTypes.string,
  /** 
   * The search parameter for the pathname if required.
   */
  search: PropTypes.string, 
  /** 
   * The form template object for the control.
   */
  formTemplate: PropTypes.array,
  /** 
   * If set, the save button will be disabled during the post and then set to green if successful for 4 seconds. If the form does not validate then the button is set to the color red.
   * Also, the loading cursor will be displayed in the save button as opposed to displaying the global busy cursor.
   */
  feedBack: PropTypes.bool,
  /** 
   * history object
   */ 
  history: PropTypes.object,
  /** 
   * If a table is being used to input controls, this is the key in the post object for the array of objects with the values for the controls.
   */
  arrKeyInBackendObject: PropTypes.string,
}

NextButton.defaultProps = {
  feedBack: true,
}

/**
 * NextButtonNavigate operates as the SaveButton except it does not perform a post. Instead, it just sets the postObject in navigation state and navigates to the input pathname.  
 * 
 * @param {object} props component props
 * @returns {NextButtonNavigate} NextButtonNavigate component
 */
export const NextButtonNavigate = props => {
  const { arrRefs, pathname, search, onBeforePost, additionalState, formTemplate, arrKeyInBackendObject, feedBack, history } = props
  const internalHistory = useHistory()
  const historyToUse = history !== undefined ? history : internalHistory

  const onClick = useCallback(async () => {
    let postObject = buildPostObject(arrRefs, formTemplate, arrKeyInBackendObject)
    if (postObject !== null) {
      if (typeof onBeforePost === 'function') {
        postObject = onBeforePost(postObject)
      }
      historyToUse.push({
        pathname,
        search,
        state: { ...additionalState, ...postObject }
      })
    } else if (feedBack) {
      setFormStatusError()
    }
  }, [])
  
  return (
    <SaveButton saveLabel='buttons.next' {...props} onClick={onClick} />
  )
}

NextButton.propTypes = {
  /**
   id for the control.
   */
  id: PropTypes.string,
  /** 
   * arrRefs is returned by the hook useFormPageHandler.
   */
  arrRefs: PropTypes.array,
  /** 
   * Called before the post with the post object as an argument. It must return the post object that will be posted to the backend.
   */
  onBeforePost: PropTypes.func,
  /** 
   * Alternative label for the save button. This will be translated.
   */
  saveLabel: PropTypes.string,
  /** 
   * The pathname to navigate to after the post.
   */
  pathname: PropTypes.string,
  /** 
   * The search parameter for the pathname if required.
   */
  search: PropTypes.string, 
  /** 
   * The form template object for the control.
   */
  formTemplate: PropTypes.array,
  /** 
   * If set, the save button will be disabled during the post and then set to green if successful for 4 seconds. If the form does not validate then the button is set to the color red.
   * Also, the loading cursor will be displayed in the save button as opposed to displaying the global busy cursor.
   */
  feedBack: PropTypes.bool,
  /** 
   * history object
   */ 
  history: PropTypes.object,
  /** 
   * Additional state for the state object used in the history.push.
   */
  additionalState: PropTypes.object,
  /** 
   * If a table is being used to input controls, this is the key in the post object for the array of objects with the values for the controls.
   */
  arrKeyInBackendObject: PropTypes.string,
}

NextButtonNavigate.defaultProps = {
  feedBack: true,
}

export const InsertRowButton = props => {
  const { arrRefs, tableRef, arrKeyInBackendObject, label, rowFieldTemplates, rowComponents, ...finalProps } = props
  const onClick = () => {
    tableRef.insertRow(arrRefs, arrKeyInBackendObject, rowFieldTemplates, rowComponents)
  }
  return (
    <InsertRowButtonStyles {...finalProps} onClick={onClick}>
      {label || 'buttons.insert'}
    </InsertRowButtonStyles>
  )
}

InsertRowButton.propTypes = {
  /**
   id for the control.
   */
  id: PropTypes.string,
  /**
   Determines the button style.
   */
  variant: PropTypes.oneOf(['contained', 'outlined', 'text']),
  /**
   Determines the button size.
   */
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  /**
   Determines the button's color.
   */
  color: PropTypes.oneOf(['primary', 'secondary', 'success', 'error', 'info', 'warning', 'default']),
  /** 
   * arrRefs is returned by the hook useFormPageHandler.
   */
  arrRefs: PropTypes.array.isRequired,
  /** 
   * Alternative label for the save button.
   */
  label: PropTypes.string,
  /** 
   * If a table is being used to input controls, this is the key in the post object for the array of objects with the values for the controls.
   */
  arrKeyInBackendObject: PropTypes.string.isRequired,
  /** 
   * rowFieldTemplates for the table.
   */
  rowFieldTemplates: PropTypes.array.isRequired,
  /** 
   * rowComponents for the table.
   */
  rowComponents: PropTypes.array.isRequired,
  /** 
   * Ref to the table.
   */
  tableRef: PropTypes.object.isRequired,
}

